import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiPlusCircle, FiEdit, FiTrash2, FiSearch, FiChevronUp, FiChevronDown } from 'react-icons/fi';
import supplierService from '../services/supplierService';
import SupplierForm from '../components/suppliers/SupplierForm';
import { useAuth } from '../context/AuthContext';
import useListView from '../hooks/useListView';

const SuppliersPage = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [modalError, setModalError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const { user } = useAuth();
  const canRead = user?.permissions?.includes('suppliers:read');
  const canCreate = user?.permissions?.includes('suppliers:create');
  const canUpdate = user?.permissions?.includes('suppliers:update');
  const canDelete = user?.permissions?.includes('suppliers:delete');

  const {
    filters,
    sort,
    pagination,
    query,
    setFilter,
    setSort,
    setPage,
    setLimit,
    applyServerPagination,
  } = useListView({
    initialFilters: {
      search: '',
      status: '',
    },
    initialSort: {
      field: 'name',
      direction: 'asc',
    },
    initialPagination: {
      page: 1,
      limit: 25,
    },
  });

  const loadSuppliers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await supplierService.fetchSuppliers(query);
      setSuppliers(response.data || []);
      applyServerPagination(response.pagination || {});
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to fetch suppliers.');
    } finally {
      setLoading(false);
    }
  }, [applyServerPagination, query]);

  useEffect(() => {
    const normalizedSearch = searchInput.trim();
    const activeSearch = String(filters.search || '').trim();
    if (normalizedSearch === activeSearch) {
      return undefined;
    }
    const timeout = setTimeout(() => {
      setFilter('search', normalizedSearch, { resetPage: true });
    }, 250);
    return () => clearTimeout(timeout);
  }, [searchInput, filters.search, setFilter]);

  useEffect(() => {
    if (canRead) {
      loadSuppliers();
    }
  }, [loadSuppliers, canRead]);

  const sortIcon = useCallback((field) => {
    if (sort.field !== field) return null;
    return sort.direction === 'asc' ? <FiChevronUp className="inline ml-1" /> : <FiChevronDown className="inline ml-1" />;
  }, [sort.direction, sort.field]);

  const handleSort = useCallback((field) => {
    setSort(field);
  }, [setSort]);

  const totalCount = Number(pagination?.total) || 0;
  const currentPage = Number(pagination?.page) || 1;
  const totalPages = Number(pagination?.pages) || 1;
  const pageSize = Number(pagination?.limit) || 25;
  const visibleStart = suppliers.length > 0 ? ((currentPage - 1) * pageSize) + 1 : 0;
  const visibleEnd = suppliers.length > 0 ? visibleStart + suppliers.length - 1 : 0;

  const pageSizeOptions = useMemo(() => [25, 50, 100], []);

  const handleOpenModal = (supplier = null) => {
    if (supplier && !canUpdate) {
      setError('You do not have permission to update suppliers.');
      return;
    }
    setSelectedSupplier(supplier);
    setModalError('');
    setModalOpen(true);
  };

  const handleSaveSupplier = async (form) => {
    if (selectedSupplier?._id && !canUpdate) {
      setModalError('You do not have permission to update suppliers.');
      return;
    }
    if (!selectedSupplier?._id && !canCreate) {
      setModalError('You do not have permission to create suppliers.');
      return;
    }
    try {
      if (selectedSupplier?._id) {
        await supplierService.updateSupplier(selectedSupplier._id, form);
      } else {
        await supplierService.createSupplier(form);
      }
      setModalOpen(false);
      setSelectedSupplier(null);
      setModalError('');
      loadSuppliers();
    } catch (err) {
      setModalError(err.response?.data?.message || 'Unable to save supplier.');
    }
  };

  const handleDeleteSupplier = async (supplierId) => {
    if (!canDelete) {
      setError('You do not have permission to delete suppliers.');
      return;
    }
    if (!window.confirm('Delete this supplier? This cannot be undone.')) {
      return;
    }
    try {
      await supplierService.deleteSupplier(supplierId);
      loadSuppliers();
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to delete supplier.');
    }
  };

  if (!canRead) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold text-red-600">Access Denied</h1>
        <p className="mt-2">You do not have the required permissions to view suppliers.</p>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Supplier Directory</h1>
          <p className="text-gray-500">Manage supplier contacts, payment terms, and lead times.</p>
        </div>
        {canCreate && (
          <button
            onClick={() => handleOpenModal(null)}
            className="inline-flex items-center bg-primary text-white px-4 py-2 rounded-md shadow hover:bg-primary-dark transition"
          >
            <FiPlusCircle className="mr-2" />
            Add Supplier
          </button>
        )}
      </div>

      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <FiSearch className="absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search suppliers..."
              className="pl-10 pr-4 py-2 border rounded-md w-full"
            />
          </div>
          <div>
            <select
              value={filters.status || ''}
              onChange={(e) => setFilter('status', e.target.value)}
              className="border rounded-md w-full py-2 px-3"
            >
              <option value="">All statuses</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
          <div className="flex items-center justify-end gap-2">
            <label className="text-sm text-gray-500">Rows</label>
            <select
              value={pageSize}
              onChange={(e) => setLimit(e.target.value)}
              className="border rounded-md py-2 px-3 text-sm"
            >
              {pageSizeOptions.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {error && <p className="bg-red-100 text-red-700 p-3 rounded mb-4">{error}</p>}

      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                <button type="button" onClick={() => handleSort('name')} className="hover:text-gray-700">
                  Name{sortIcon('name')}
                </button>
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                <button type="button" onClick={() => handleSort('primaryContactName')} className="hover:text-gray-700">
                  Contact{sortIcon('primaryContactName')}
                </button>
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Payment Terms</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                <button type="button" onClick={() => handleSort('defaultLeadTimeDays')} className="hover:text-gray-700">
                  Lead Time{sortIcon('defaultLeadTimeDays')}
                </button>
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">
                <button type="button" onClick={() => handleSort('status')} className="hover:text-gray-700">
                  Status{sortIcon('status')}
                </button>
              </th>
              <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading && (
              <tr>
                <td colSpan={6} className="px-6 py-6 text-center text-gray-500">
                  Loading suppliers...
                </td>
              </tr>
            )}
            {!loading && suppliers.length === 0 && (
              <tr>
                <td colSpan={6} className="px-6 py-6 text-center text-gray-500">
                  No suppliers found.
                </td>
              </tr>
            )}
            {suppliers.map((supplier) => (
              <tr key={supplier._id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-semibold text-gray-900">{supplier.name}</div>
                  <div className="text-xs text-gray-500">{supplier.code}</div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-900">{supplier.primaryContactName || '—'}</div>
                  <div className="text-xs text-gray-500">{supplier.primaryEmail}</div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">{supplier.paymentTerms || 'Net 0'}</td>
                <td className="px-6 py-4 text-sm text-gray-900">{supplier.defaultLeadTimeDays || 0} days</td>
                <td className="px-6 py-4">
                  <span
                    className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      supplier.status === 'Active'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {supplier.status}
                  </span>
                </td>
                <td className="px-6 py-4 text-right text-sm font-medium space-x-3">
                  {canUpdate && (
                    <button
                      onClick={() => handleOpenModal(supplier)}
                      className="inline-flex items-center text-indigo-600 hover:text-indigo-900"
                    >
                      <FiEdit className="mr-1" /> Edit
                    </button>
                  )}
                  {canDelete && (
                    <button
                      onClick={() => handleDeleteSupplier(supplier._id)}
                      className="inline-flex items-center text-red-600 hover:text-red-900"
                    >
                      <FiTrash2 className="mr-1" /> Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-sm text-gray-600">
        <div>
          {totalCount > 0
            ? `Showing ${visibleStart}-${visibleEnd} of ${totalCount} suppliers`
            : 'No suppliers to display'}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setPage(currentPage - 1)}
            disabled={loading || currentPage <= 1}
            className="px-3 py-1.5 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <span>Page {currentPage} of {Math.max(totalPages, 1)}</span>
          <button
            type="button"
            onClick={() => setPage(currentPage + 1)}
            disabled={loading || currentPage >= totalPages}
            className="px-3 py-1.5 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-full p-6">
            <h2 className="text-2xl font-bold mb-4">
              {selectedSupplier ? 'Edit Supplier' : 'Add Supplier'}
            </h2>
            {modalError && <p className="bg-red-100 text-red-700 p-3 rounded mb-4">{modalError}</p>}
            <SupplierForm
              supplier={selectedSupplier}
              onSave={handleSaveSupplier}
              onCancel={() => {
                setModalOpen(false);
                setSelectedSupplier(null);
                setModalError('');
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default SuppliersPage;
